import { Box, Typography, Divider } from '@mui/material';
import moment from 'moment';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import QueryBuilderRoundedIcon from '@mui/icons-material/QueryBuilderRounded';

import {
  FORTA_BLUE,
  FORTA_GRAY4,
  FORTA_ERROR,
  FORTA_GRAY3,
  FORTA_SUCCESS,
  FORTA_WHITE,
  FORTA_YELLOW,
} from '../../../utils/colors';
import { getGuardianSignatureWarningVisibility } from '../../../pages/session/btSessionNotes/utils';
import { formatEntityId, formatSecondsToHHMM, history } from '../../../helpers';
import DurationIcon from '../../svgs/DurationIcon';
import StatusIconRenderer from '../StatusIconRenderer';
import { ServiceCodes } from '../billings/claims/constants';
import { ENTITY_PREFIX, BTSessionStatus, BAYS } from '../../../constants';
import { CQAReviewStatus, Verdict } from '../qa/constants';
import ClearSignatureIcon from '../../svgs/ClearSignatureIcon';
import ScoreIcon from '../../../assets/images/score.svg';
import BtIcon from '../../../assets/images/bt.svg';
import { isDateOlderThan24Hours } from './utils';

import type { ISessionMinified } from '../../../utils/types';

interface ISessionNoteItemProps {
  session: ISessionMinified;
  reviewItem?: boolean;
  cqaReviewCard?: boolean;
  sx?: any;
}

function SessionNoteItem({
  session,
  reviewItem = false,
  cqaReviewCard = false,
  sx = {},
}: ISessionNoteItemProps) {
  const {
    id: sessionId,
    client,
    session_provider,
    status,
    date_start,
    expected_duration,
    clinical_qa_review,
  } = session;

  const handleClick = () => {
    if (reviewItem) {
      history.navigate(`/sessions/${sessionId}/review`);
    } else {
      history.navigate(`/sessions/${sessionId}`);
    }
  };

  const showRedBorder =
    cqaReviewCard &&
    session?.clinical_qa_review?.date_start &&
    isDateOlderThan24Hours(session?.clinical_qa_review?.date_start);

  const isGuardianSignatureWarningVisible = getGuardianSignatureWarningVisibility(session);

  const isReviewed = Boolean(clinical_qa_review);

  const isSigned = clinical_qa_review?.status === CQAReviewStatus.Acknowledged;

  const isAwaitingFinalization =
    status === BTSessionStatus.AwaitingReview &&
    clinical_qa_review?.status === CQAReviewStatus.Acknowledged;

  const isCompleted =
    status === BTSessionStatus.Completed &&
    clinical_qa_review?.status === CQAReviewStatus.Acknowledged;

  const dataTestIdPrefix = reviewItem ? 'sessionNoteReview' : 'sessionNote';

  const showSignature =
    (status === BTSessionStatus.Started ||
      status === BTSessionStatus.AwaitingGuardianSignature ||
      status === BTSessionStatus.EditsRequested ||
      isReviewed) &&
    !isCompleted &&
    !cqaReviewCard;

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        backgroundColor: FORTA_WHITE,
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        color: FORTA_GRAY4,
        px: 2,
        py: 1,
        height: 'auto',
        minWidth: 230,
        border: `${
          isGuardianSignatureWarningVisible || showRedBorder ? `1px solid ${FORTA_ERROR}` : 'none'
        }`,
        ...sx,
      }}
      onClick={handleClick}
      data-testid={`${dataTestIdPrefix}-${client?.first_name}_${client?.last_name}_${status.replace(
        / /g,
        '_',
      )}`}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          my: 1,
        }}
      >
        <StatusIconRenderer
          iconHeight={24}
          iconWidth={24}
          procedureCode={ServiceCodes.SessionProcedureCode}
          status={status}
          isAwaitingFinalization={isAwaitingFinalization}
        />
        <Typography display="inline" ml={1}>
          {`${client?.first_name} ${client?.last_name}`}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
        <img width={16} src={BtIcon} alt="BT icon" />
        <Typography display="inline" ml={0.5} fontSize={14}>
          {session_provider?.first_name} {session_provider?.last_name}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          flexWrap: 'nowrap',
          my: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <CalendarTodayOutlinedIcon sx={{ color: FORTA_BLUE.primary, height: 14, width: 14 }} />
          <Typography display="inline" color={FORTA_GRAY3} ml={0.5} fontSize={14}>
            {`${moment(date_start).format('MM/DD/YY')}`}
          </Typography>
        </Box>
        <Divider
          sx={{
            color: FORTA_GRAY3,
            borderWidth: '1px',
            height: '16px',
            mx: 1,
          }}
          orientation="vertical"
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <QueryBuilderRoundedIcon sx={{ color: FORTA_BLUE.primary, height: 16, width: 16 }} />
          <Typography
            display="inline"
            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            color={FORTA_GRAY3}
            ml={0.5}
            fontSize={14}
          >
            {`${moment(date_start).format('hh:mm a')}`}
          </Typography>
        </Box>
        <Divider
          sx={{
            color: FORTA_GRAY3,
            borderWidth: '1px',
            height: '16px',
            mx: 1,
          }}
          orientation="vertical"
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <DurationIcon color={FORTA_BLUE.primary} width={18} height={18} viewBox="0 0 13 10" />
          <Typography
            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            color={FORTA_GRAY3}
            ml={0.5}
            fontSize={14}
          >
            {`${formatSecondsToHHMM(expected_duration)}`}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          my: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 24,
            px: 1,
            mr: 2,
            backgroundColor: FORTA_BLUE.light3,
            borderRadius: '16px',
            color: FORTA_BLUE.primary,
          }}
        >
          <Typography display="inline" fontSize={14} fontWeight={700} px={0.5}>
            {formatEntityId(sessionId ?? '', ENTITY_PREFIX.SessionNotes)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex' }}>
          {/* Show score if a session is reviewed but not for completed sessions */}
          {(Boolean(clinical_qa_review?.score) || clinical_qa_review?.score === 0) &&
            !isCompleted &&
            !cqaReviewCard && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1,
                    width: '56px',
                    borderRadius: '20px',
                    backgroundColor:
                      clinical_qa_review?.verdict === Verdict.Pass ? FORTA_SUCCESS : FORTA_ERROR,
                  }}
                >
                  <img src={ScoreIcon} alt="score" width={16} height={16} />
                  <Typography fontWeight={700} fontSize={14} color={FORTA_WHITE} ml={0.8}>
                    {clinical_qa_review?.score}
                  </Typography>
                </Box>
              </>
            )}
          {/* Show Signature icon if a session is started or reviewed but not for completed sessions */}

          {showSignature && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 'auto',
                height: 25,
                pr: isSigned ? 0 : 1,
                ml: 1,
                backgroundColor: isSigned ? 'transparent' : FORTA_BLUE.light3,
                border: `${
                  isGuardianSignatureWarningVisible ? `1px solid ${FORTA_ERROR}` : 'none'
                }`,
                borderRadius: '16px',
              }}
            >
              <Box
                sx={{
                  backgroundColor: isSigned
                    ? FORTA_SUCCESS
                    : isGuardianSignatureWarningVisible
                      ? FORTA_ERROR
                      : FORTA_YELLOW,
                  width: '25px',
                  height: '25px',
                  borderRadius: '50%',
                  py: 0.4,
                  p: 0.55,
                }}
              >
                <ClearSignatureIcon
                  color={
                    isSigned || isGuardianSignatureWarningVisible ? FORTA_WHITE : FORTA_BLUE.primary
                  }
                />
              </Box>
              {isSigned ? null : (
                <Typography
                  ml={1}
                  fontSize={12}
                  color={isGuardianSignatureWarningVisible ? FORTA_ERROR : FORTA_BLUE.primary}
                >
                  {session.bay === BAYS.BAY_3
                    ? 'BT'
                    : BTSessionStatus.AwaitingGuardianSignature === status
                      ? 'Guardian'
                      : 'VBT'}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default SessionNoteItem;
